import React from "react";
import { motion } from "framer-motion";
import { Cake, PartyPopper, Sparkles, Download, Share2, Loader2, CalendarClock } from "lucide-react";
import { toPng } from "html-to-image";
import { IMAGES, toDataUrl } from "../lib/images";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import FullImage from "../components/site/FullImage";
import Newsletter from "../components/site/Newsletter";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const dateLabel = (s) => {
  if (s.date) return s.date;
  if (s.birth_month) return `${MONTHS[s.birth_month - 1]}${s.birth_day ? " " + s.birth_day : ""}`;
  return "";
};

// Days until the next occurrence of a month/day birthday (0 = today).
const daysUntil = (m, d) => {
  if (!m || !d) return Infinity;
  const now = new Date();
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let next = new Date(now.getFullYear(), m - 1, d);
  if (next < today0) next = new Date(now.getFullYear() + 1, m - 1, d);
  return Math.round((next - today0) / 86400000);
};

function ShoutoutCard({ s, onCard, busy, highlight }) {
  return (
    <div
      className={`group flex flex-col rounded-2xl overflow-hidden bg-cream transition-all duration-300 hover:-translate-y-1 ${
        highlight ? "border-2 border-gold shadow-lg shadow-gold/10" : "border border-parchment hover:border-gold"
      }`}
      data-testid={`shoutout-card-${s.id}`}
    >
      {highlight && (
        <div className="bg-gold text-night text-center py-1.5 text-xs font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-1.5">
          <PartyPopper className="w-3.5 h-3.5" /> Birthday Today
        </div>
      )}
      {s.video ? (
        <video
          src={s.video}
          poster={s.image || undefined}
          ref={(el) => { if (el) el.muted = true; }}
          controls
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid={`shoutout-video-${s.id}`}
          className="w-full aspect-[4/5] object-cover bg-night"
        />
      ) : s.image ? (
        <FullImage src={s.image} alt={s.name} className="aspect-[4/5]" imgClassName="group-hover:scale-105 transition-transform duration-700" />
      ) : (
        <div className="relative aspect-[4/5] overflow-hidden bg-forest-deep grain-overlay grid place-items-center">
          <span className="grid place-items-center w-20 h-20 rounded-full border border-gold/40 text-gold">
            <Cake className="w-9 h-9" />
          </span>
        </div>
      )}
      <div className="p-6 text-center flex-1 flex flex-col">
        {dateLabel(s) && <p className="text-xs uppercase tracking-[0.2em] text-gold-soft mb-2">{dateLabel(s)}</p>}
        <h3 className="font-serif text-2xl text-ink">{s.name}</h3>
        {s.title && <p className="text-sm text-gold mt-1">{s.title}</p>}
        {s.message && <p className="text-sm text-ink-muted mt-3 leading-relaxed whitespace-pre-line">{s.message}</p>}
        <p className="mt-4 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-forest">
          <Sparkles className="w-3.5 h-3.5" /> ÀsÀNigHT Worldwide celebrates you
        </p>
        <div className="flex justify-center gap-2 mt-auto pt-5">
          <button
            onClick={() => onCard(s, "download")}
            disabled={busy !== ""}
            data-testid={`shoutout-download-${s.id}`}
            className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-4 py-2 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50"
          >
            {busy === `download-${s.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Card
          </button>
          <button
            onClick={() => onCard(s, "share")}
            disabled={busy !== ""}
            data-testid={`shoutout-share-${s.id}`}
            className="inline-flex items-center gap-2 border border-parchment text-ink text-sm font-semibold px-4 py-2 rounded-full hover:border-gold hover:text-forest transition-colors disabled:opacity-50"
          >
            {busy === `share-${s.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Share
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Celebrate() {
  useSEO(
    "Birthday Shoutouts",
    "ÀsÀNigHT Worldwide celebrates our community — birthday shoutouts and heartfelt wishes to the people who make our culture shine."
  );

  const [shoutouts, setShoutouts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [banner, setBanner] = React.useState("");
  React.useEffect(() => { api.get("/site-settings").then((r) => { if (r.data?.banner_celebrate) setBanner(r.data.banner_celebrate); }).catch(() => {}); }, []);
  const [shareTarget, setShareTarget] = React.useState(null);
  const [cardImg, setCardImg] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const cardRef = React.useRef(null);

  React.useEffect(() => {
    api
      .get("/shoutouts")
      .then((r) => setShoutouts(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const now = new Date();
  const tm = now.getMonth() + 1;
  const td = now.getDate();
  const isToday = (s) => s.birth_month === tm && s.birth_day === td;
  const todays = shoutouts.filter(isToday);
  const others = shoutouts.filter((s) => !isToday(s));
  const upcoming = shoutouts
    .map((s) => ({ s, days: daysUntil(s.birth_month, s.birth_day) }))
    .filter((x) => x.days >= 1 && x.days <= 7)
    .sort((a, b) => a.days - b.days);

  const onCard = async (s, action) => {
    setShareTarget(s);
    setBusy(`${action}-${s.id}`);
    setCardImg(s.image ? await toDataUrl(s.image) : null);
    await new Promise((res) => setTimeout(res, 120));
    try {
      if (!cardRef.current) return;
      const url = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true, backgroundColor: "#12100e" });
      if (!url) return;
      if (action === "share") {
        const blob = await (await fetch(url)).blob();
        const file = new File([blob], `happy-birthday-${s.name}.png`, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `Happy Birthday ${s.name}`, text: `ÀsÀNigHT Worldwide celebrates ${s.name}!` });
          return;
        }
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = `happy-birthday-${s.name}.png`;
      a.click();
    } catch (e) {
      /* user cancelled etc. */
    } finally {
      setBusy("");
    }
  };

  return (
    <div>
      {/* HERO */}
      <header className="relative min-h-[52vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={banner || IMAGES.drummers} alt="Celebration" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/80 to-night/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-16 w-full">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5 inline-flex items-center gap-2">
            <PartyPopper className="w-4 h-4" /> Ẹ kú ọjọ́ ìbí — Happy Birthday
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-6xl text-cream leading-tight text-shadow-hero"
          >
            ÀsÀNigHT Worldwide Celebrates You
          </motion.h1>
          <p className="mt-5 text-cream/80 text-lg max-w-2xl">
            Honouring the birthdays and milestones of the people who make our community and
            culture shine.
          </p>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-20">
        {loading ? (
          <p className="text-ink-muted text-center py-16">Loading…</p>
        ) : shoutouts.length === 0 ? (
          <Reveal>
            <div className="text-center border border-dashed border-parchment rounded-2xl py-14 px-6 bg-sand" data-testid="celebrate-empty">
              <span className="grid place-items-center w-16 h-16 rounded-full bg-gold/15 text-gold mx-auto mb-4">
                <Cake className="w-7 h-7" />
              </span>
              <p className="font-serif text-2xl text-ink mb-2">No Shoutouts Yet</p>
              <p className="text-ink-muted max-w-xl mx-auto">
                Birthday celebrations for our members and loved ones will appear here soon. Check
                back to join the joy!
              </p>
            </div>
          </Reveal>
        ) : (
          <>
            {upcoming.length > 0 && (
              <Reveal>
                <div className="mb-14 rounded-2xl border border-parchment bg-sand/60 p-6" data-testid="upcoming-birthdays">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarClock className="w-5 h-5 text-forest" />
                    <h2 className="font-serif text-2xl text-ink">Coming Up This Week</h2>
                  </div>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {upcoming.map(({ s, days }) => (
                      <div key={s.id} data-testid={`upcoming-item-${s.id}`} className="shrink-0 flex items-center gap-3 bg-cream border border-parchment rounded-full pl-1.5 pr-5 py-1.5">
                        <span className="grid place-items-center w-11 h-11 rounded-full bg-gold/15 text-gold shrink-0 overflow-hidden">
                          {s.image ? <img src={s.image} alt="" className="w-full h-full object-cover" /> : <Cake className="w-5 h-5" />}
                        </span>
                        <div className="leading-tight">
                          <p className="font-serif text-base text-ink whitespace-nowrap">{s.name}</p>
                          <p className="text-xs text-gold whitespace-nowrap">
                            {dateLabel(s)} · {days === 1 ? "tomorrow" : `in ${days} days`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}

            {todays.length > 0 && (
              <div className="mb-16" data-testid="todays-birthdays">
                <Reveal>
                  <div className="flex items-center gap-3 mb-8">
                    <span className="grid place-items-center w-12 h-12 rounded-full bg-gold text-night shrink-0">
                      <PartyPopper className="w-5 h-5" />
                    </span>
                    <div>
                      <h2 className="font-serif text-3xl md:text-4xl text-ink">Today's Birthdays</h2>
                      <p className="text-sm text-ink-muted">Ẹ kú ọjọ́ ìbí! Celebrating today with our community.</p>
                    </div>
                  </div>
                </Reveal>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {todays.map((s, i) => (
                    <Reveal key={s.id} delay={i * 0.08}>
                      <ShoutoutCard s={s} onCard={onCard} busy={busy} highlight />
                    </Reveal>
                  ))}
                </div>
              </div>
            )}

            {others.length > 0 && (
              <div>
                {todays.length > 0 && (
                  <Reveal>
                    <h2 className="font-serif text-3xl md:text-4xl text-ink mb-8">More Celebrations</h2>
                  </Reveal>
                )}
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {others.map((s, i) => (
                    <Reveal key={s.id} delay={i * 0.08}>
                      <ShoutoutCard s={s} onCard={onCard} busy={busy} />
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Hidden branded Happy Birthday share card */}
      {shareTarget && (
        <div aria-hidden="true" style={{ position: "fixed", top: 0, left: "-20000px", pointerEvents: "none" }}>
          <div
            ref={cardRef}
            data-testid="shoutout-share-card"
            style={{
              width: 1080,
              height: 1080,
              background: "linear-gradient(155deg, #1b3327 0%, #12100e 60%)",
              color: "#F5EFE6",
              padding: 90,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", inset: 40, border: "2px solid rgba(212,169,74,0.35)", borderRadius: 28, pointerEvents: "none" }} />
            <div style={{ position: "relative", textAlign: "center" }}>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 42, color: "#D4A94A" }}>ÀsÀNigHT</div>
              <div style={{ fontSize: 15, letterSpacing: 8, color: "rgba(245,239,230,0.6)", marginTop: 4 }}>WORLDWIDE</div>
              <div style={{ fontSize: 22, letterSpacing: 5, textTransform: "uppercase", color: "#E8D5A8", marginTop: 34 }}>
                Ẹ kú ọjọ́ ìbí · Happy Birthday
              </div>
            </div>

            <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
              {cardImg && (
                <img
                  src={cardImg}
                  alt=""
                  style={{ width: 360, height: 360, objectFit: "cover", borderRadius: "50%", border: "5px solid rgba(212,169,74,0.6)", marginBottom: 40 }}
                />
              )}
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", color: "#D4A94A", fontSize: shareTarget.name.length > 22 ? 60 : 76, lineHeight: 1.1, textAlign: "center" }}>
                {shareTarget.name}
              </div>
              {shareTarget.title && (
                <div style={{ fontSize: 30, color: "#E8D5A8", marginTop: 16, textAlign: "center" }}>{shareTarget.title}</div>
              )}
              {shareTarget.message && (
                <div style={{ fontSize: 27, lineHeight: 1.45, color: "rgba(245,239,230,0.82)", marginTop: 28, textAlign: "center", maxWidth: 760 }}>
                  {shareTarget.message}
                </div>
              )}
            </div>

            <div style={{ position: "relative", borderTop: "1px solid rgba(245,239,230,0.15)", paddingTop: 26, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 24, color: "#D4A94A", letterSpacing: 1 }}>asanightworldwide.com</div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: 24, color: "rgba(245,239,230,0.5)" }}>
                Celebrating you
              </div>
            </div>
          </div>
        </div>
      )}

      <Newsletter />
    </div>
  );
}
